/**
 * Markdown — lightweight renderer for AI responses.
 *
 * Layer: C (UX/CLI)
 *
 * Handles: fenced code blocks, inline code, bold, italic,
 * links, headers, unordered/ordered lists, blockquotes, hr.
 *
 * No external dependencies. React components only.
 * NOT a full CommonMark parser — covers 95% of LLM output.
 */

import { useState, type ReactNode } from 'react';

// ─── Code Block with copy button ─────────────────────────────────────

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="group relative my-3 rounded-md border border-border-subtle bg-[#0d0d14] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-subtle bg-bg-tertiary/50 px-3 py-1.5">
        <span className="font-mono text-[12px] text-text-muted">{language || 'code'}</span>
        <button
          onClick={handleCopy}
          className="text-[12px] text-text-muted transition-colors hover:text-text-secondary"
        >
          {copied ? 'Kopierat ✓' : 'Kopiera'}
        </button>
      </div>
      {/* Code */}
      <pre className="overflow-x-auto p-3 text-[14px] leading-relaxed">
        <code className="font-mono text-text-primary">{code}</code>
      </pre>
    </div>
  );
}

// ─── Inline parsing ──────────────────────────────────────────────────

/**
 * Parse inline markdown: **bold**, *italic*, `code`, [links](url)
 * Returns array of ReactNodes.
 */
function parseInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Regex matches: **bold**, *italic*, `inline code`, [text](url)
  const re = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // **bold**
      nodes.push(
        <strong key={key++} className="font-semibold text-text-primary">
          {match[2]}
        </strong>,
      );
    } else if (match[3]) {
      // *italic*
      nodes.push(
        <em key={key++} className="italic text-text-secondary">
          {match[4]}
        </em>,
      );
    } else if (match[5]) {
      // `inline code`
      nodes.push(
        <code
          key={key++}
          className="rounded bg-bg-tertiary px-1.5 py-0.5 font-mono text-[0.9em] text-accent"
        >
          {match[6]}
        </code>,
      );
    } else if (match[7]) {
      // [text](url)
      nodes.push(
        <a
          key={key++}
          href={match[9]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {match[8]}
        </a>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}

// ─── Block parsing ───────────────────────────────────────────────────

interface Block {
  type: 'paragraph' | 'code' | 'heading' | 'list' | 'blockquote' | 'hr';
  content: string;
  language?: string;
  level?: number;
  ordered?: boolean;
  items?: string[];
}

function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  const lines = markdown.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    const codeMatch = line.match(/^```(\w*)/);
    if (codeMatch) {
      const language = codeMatch[1] || '';
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: 'code', content: codeLines.join('\n'), language });
      i++; // skip closing ```
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        content: headingMatch[2],
        level: headingMatch[1].length,
      });
      i++;
      continue;
    }

    // HR
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      blocks.push({ type: 'hr', content: '' });
      i++;
      continue;
    }

    // Unordered list
    if (/^[\s]*[-*+]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[\s]*[-*+]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[\s]*[-*+]\s/, ''));
        i++;
      }
      blocks.push({ type: 'list', content: '', ordered: false, items });
      continue;
    }

    // Ordered list
    if (/^[\s]*\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[\s]*\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[\s]*\d+\.\s/, ''));
        i++;
      }
      blocks.push({ type: 'list', content: '', ordered: true, items });
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
      continue;
    }

    // Empty line — skip
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph — collect contiguous non-empty lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('```') &&
      !lines[i].match(/^#{1,4}\s/) &&
      !lines[i].match(/^[-*+]\s/) &&
      !lines[i].match(/^\d+\.\s/) &&
      !lines[i].startsWith('>') &&
      !/^(-{3,}|\*{3,}|_{3,})$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', content: paraLines.join('\n') });
    }
  }

  return blocks;
}

// ─── Render ──────────────────────────────────────────────────────────

function renderBlock(block: Block, index: number): ReactNode {
  switch (block.type) {
    case 'code':
      return <CodeBlock key={index} language={block.language ?? ''} code={block.content} />;

    case 'heading': {
      const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3' | 'h4';
      const sizes: Record<number, string> = {
        1: 'text-lg font-semibold mt-5 mb-2',
        2: 'text-base font-semibold mt-4 mb-2',
        3: 'text-sm font-semibold mt-3 mb-1',
        4: 'text-sm font-medium mt-2 mb-1',
      };
      return (
        <Tag key={index} className={`${sizes[block.level ?? 2]} text-text-primary`}>
          {parseInline(block.content)}
        </Tag>
      );
    }

    case 'list':
      if (block.ordered) {
        return (
          <ol key={index} className="my-2 ml-5 list-decimal space-y-1 text-text-primary">
            {block.items?.map((item, j) => (
              <li key={j} className="pl-1">{parseInline(item)}</li>
            ))}
          </ol>
        );
      }
      return (
        <ul key={index} className="my-2 ml-5 list-disc space-y-1 text-text-primary">
          {block.items?.map((item, j) => (
            <li key={j} className="pl-1">{parseInline(item)}</li>
          ))}
        </ul>
      );

    case 'blockquote':
      return (
        <blockquote
          key={index}
          className="my-3 border-l-2 border-accent/40 pl-4 text-text-secondary italic"
        >
          {parseInline(block.content)}
        </blockquote>
      );

    case 'hr':
      return <hr key={index} className="my-4 border-border-subtle" />;

    case 'paragraph':
    default:
      return (
        <p key={index} className="my-2 leading-relaxed text-text-primary">
          {parseInline(block.content)}
        </p>
      );
  }
}

// ─── Exported Component ──────────────────────────────────────────────

export function Markdown({ content }: { content: string }) {
  if (!content) return null;

  const blocks = parseBlocks(content);

  return <div className="markdown-content">{blocks.map(renderBlock)}</div>;
}
